import { catalogueVisitorAuthHeaderValue } from "./catalogueVisitorSession";
import { pickProductListThumbUrl } from "../../products/previewMedia";

export function pickPrimaryProductImage(product: any): string | null {
  const still = pickProductListThumbUrl(product?.thumbnailDetails?.media);
  if (still) return still;
  const media = product?.thumbnailDetails?.media;
  if (!Array.isArray(media) || media.length === 0) {
    return null;
  }
  const pinned = media.filter(
    (m: any) =>
      (m?.pin === 1 || m?.pin === "1") &&
      (m?.show === 1 || m?.show === "1")
  );
  const shown = media.filter((m: any) => m?.show === 1 || m?.show === "1");
  const pick = pinned[0] || shown[0] || media[0];
  return pick?.mediaURL || null;
}

/**
 * The grid-sized version of a piece photo (about 800px wide, enough for a tile on a phone): the
 * marking proxy takes size=grid, and an unsigned 1200px copy has an 800px copy beside it. Anything
 * else is returned as it is.
 */
export function gridImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.includes("/catalogue-media/public/") && !/[?&]size=/.test(url)) {
    return `${url}${url.includes("?") ? "&" : "?"}size=grid`;
  }
  if (!url.includes("?") && /\/derivatives\/v1\/(.+\/)?1200\.(jpg|png)$/.test(url)) {
    return url.replace(/\/1200\.(jpg|png)$/, "/800.$1");
  }
  return url;
}

export function shouldAttachStudioJwt(search: string): boolean {
  const query = search.startsWith("?") ? search.slice(1) : search;
  return !new URLSearchParams(query).get("link");
}

export function studioJwtAuthorizationValue(): string | undefined {
  if (typeof localStorage === "undefined") return undefined;
  const token = localStorage.getItem("token");
  return token ? token : undefined;
}

export function buildPublicAuthHeaders(options?: { includeStudioJwt?: boolean }): HeadersInit {
  const headers: Record<string, string> = {};
  const search = typeof window !== "undefined" ? window.location.search : "";
  const includeStudio = options?.includeStudioJwt ?? shouldAttachStudioJwt(search);
  if (includeStudio) {
    const studio = studioJwtAuthorizationValue();
    if (studio) {
      headers["Authorization"] = `Bearer ${studio}`;
    }
  }
  const visitor = catalogueVisitorAuthHeaderValue();
  if (visitor) {
    headers["X-HKP-Catalogue-Visitor"] = visitor;
  }
  return headers;
}

export function buildPublicJsonHeaders(options?: { includeStudioJwt?: boolean }): HeadersInit {
  return {
    "Content-Type": "application/json",
    ...buildPublicAuthHeaders(options),
  } as HeadersInit;
}

export function legacyCatalogueAccessToken(params: URLSearchParams): string | undefined {
  return params.get("accessToken") || params.get("token") || undefined;
}

export function linkTokenFromLegacySession(catalogueId?: string, sessionToken?: string): string | undefined {
  if (!catalogueId || !sessionToken) return undefined;
  const parts = sessionToken.split(":");
  if (parts.length !== 3 || parts[0] !== catalogueId) return undefined;
  return parts[1]?.trim() || undefined;
}

export function publicCatalogueSessionKey(catalogueId?: string, linkToken?: string): string | null {
  if (!catalogueId || !linkToken) return null;
  return `hkp_public_catalogue_session:${catalogueId}:${linkToken}`;
}

export function loadPublicCatalogueSession(catalogueId?: string, linkToken?: string): string | undefined {
  const key = publicCatalogueSessionKey(catalogueId, linkToken);
  if (!key || typeof window === "undefined") return undefined;
  return window.sessionStorage.getItem(key) || undefined;
}

export function savePublicCatalogueSession(catalogueId: string, linkToken: string, sessionToken: string): void {
  const key = publicCatalogueSessionKey(catalogueId, linkToken);
  if (!key || typeof window === "undefined") return;
  window.sessionStorage.setItem(key, sessionToken);
}

export function clearPublicCatalogueSession(catalogueId?: string, linkToken?: string): void {
  const key = publicCatalogueSessionKey(catalogueId, linkToken);
  if (!key || typeof window === "undefined") return;
  window.sessionStorage.removeItem(key);
  clearPublicCatalogueGuest(catalogueId, linkToken);
}

/** Invitee label after OTP — not the catalogue-visitor JWT. */
export type PublicCatalogueGuest = {
  displayName: string;
  phone?: string;
  countryIso?: string;
};

/** E.164 digits without +. Keeps overseas numbers; does not slice to 10. */
export function normalizeGuestPhoneDigits(raw: string | undefined): string {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) return "";
  return digits;
}

function publicCatalogueGuestKey(catalogueId?: string, linkToken?: string): string | null {
  if (!catalogueId || !linkToken) return null;
  return `hkp_public_catalogue_guest:${catalogueId}:${linkToken}`;
}

export function loadPublicCatalogueGuest(
  catalogueId?: string,
  linkToken?: string
): PublicCatalogueGuest | undefined {
  const key = publicCatalogueGuestKey(catalogueId, linkToken);
  if (!key || typeof window === "undefined") return undefined;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return undefined;
    const p = JSON.parse(raw) as Partial<PublicCatalogueGuest>;
    const displayName = typeof p.displayName === "string" ? p.displayName.trim() : "";
    const phone = normalizeGuestPhoneDigits(p.phone);
    const countryIso = typeof p.countryIso === "string" ? p.countryIso.trim() : "";
    if (!displayName && !phone) return undefined;
    return {
      displayName: displayName || "Verified guest",
      phone: phone || undefined,
      countryIso: countryIso || undefined,
    };
  } catch {
    return undefined;
  }
}

export function savePublicCatalogueGuest(
  catalogueId: string,
  linkToken: string,
  guest: PublicCatalogueGuest
): void {
  const key = publicCatalogueGuestKey(catalogueId, linkToken);
  if (!key || typeof window === "undefined") return;
  const phone = normalizeGuestPhoneDigits(guest.phone);
  const countryIso = guest.countryIso ? String(guest.countryIso).trim() : "";
  window.sessionStorage.setItem(
    key,
    JSON.stringify({
      displayName: (guest.displayName || "Verified guest").trim(),
      phone: phone || undefined,
      countryIso: countryIso || undefined,
    })
  );
}

export function clearPublicCatalogueGuest(catalogueId?: string, linkToken?: string): void {
  const key = publicCatalogueGuestKey(catalogueId, linkToken);
  if (!key || typeof window === "undefined") return;
  window.sessionStorage.removeItem(key);
}

/** Public catalogue list — uses fetch (not getApi) so 403 does not trigger global redirect. */
export async function fetchPublicCatalogueProducts(
  catalogueId: string,
  options: {
    offset: number;
    limit: number;
    sortBy?: string;
    sortOrder?: string;
    accessToken?: string;
    linkToken?: string;
    sessionToken?: string;
    /** Matches org-configured catalogue pricing tier (e.g. EXPORT, B2B). */
    priceTier?: string;
    /** Inclusive price range on the price this viewer sees. */
    minPrice?: number;
    maxPrice?: number;
    /** Type-tag ids, any-of. */
    typeTagIds?: string[];
  }
): Promise<any> {
  const base = process.env.REACT_APP_SERVERURL || "";
  const sortBy = options.sortBy || "name";
  const sortOrder = options.sortOrder || "asc";
  let url =
    `${base}/catalouge/public/${catalogueId}/products?offset=` +
    options.offset +
    "&limit=" +
    options.limit +
    "&sortBy=" +
    encodeURIComponent(sortBy) +
    "&sortOrder=" +
    encodeURIComponent(sortOrder);
  if (options.accessToken) {
    url += "&accessToken=" + encodeURIComponent(options.accessToken);
  }
  if (options.linkToken) {
    url += "&link=" + encodeURIComponent(options.linkToken);
  }
  if (options.priceTier) {
    url += "&priceTier=" + encodeURIComponent(options.priceTier);
  }
  if (options.minPrice != null && options.minPrice > 0) {
    url += "&minPrice=" + encodeURIComponent(String(options.minPrice));
  }
  if (options.maxPrice != null && options.maxPrice > 0) {
    url += "&maxPrice=" + encodeURIComponent(String(options.maxPrice));
  }
  if (options.typeTagIds && options.typeTagIds.length) {
    url += "&typeTagIds=" + encodeURIComponent(options.typeTagIds.join(","));
  }
  const headers: Record<string, string> = { ...(buildPublicAuthHeaders() as Record<string, string>) };
  if (options.sessionToken) {
    headers["X-HKP-Catalogue-Session"] = options.sessionToken;
  }
  const res = await fetch(url, { headers });
  return res.json();
}

export const PUBLIC_CATALOGUE_PAGE_SIZE = 20;

export function pageFromSearch(search: string): number {
  const query = search.startsWith("?") ? search.slice(1) : search;
  const raw = new URLSearchParams(query).get("page");
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

export function withCataloguePage(params: URLSearchParams, page: number): URLSearchParams {
  const next = new URLSearchParams(params);
  if (page <= 1) {
    next.delete("page");
  } else {
    next.set("page", String(page));
  }
  return next;
}

export function focusPieceFromSearch(search: string): string {
  const query = search.startsWith("?") ? search.slice(1) : search;
  return String(new URLSearchParams(query).get("focus") || "").trim();
}

export function withFocusPiece(params: URLSearchParams, productUniqueId: string): URLSearchParams {
  const next = new URLSearchParams(params);
  const id = productUniqueId.trim();
  if (!id) {
    next.delete("focus");
  } else {
    next.set("focus", id);
  }
  return next;
}

/**
 * Query params a shared piece link keeps: access (including the legacy accessToken form), and the
 * page and price range so a card's focus still finds it on the collection. Everything else drops.
 */
const SHARED_LINK_PARAMS = ["catalogueId", "link", "token", "accessToken", "priceTier", "page", "minPrice", "maxPrice", "tags"];

/**
 * A short link that reopens this piece in the same shared catalogue: the current page with only
 * its access params, focused on `pieceId` (the id the page already uses, an alias on client links).
 */
export function sharedPieceUrl(href: string, pieceId: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return "";
  }
  const kept = new URLSearchParams();
  for (const key of SHARED_LINK_PARAMS) {
    const v = url.searchParams.get(key);
    if (v) kept.set(key, v);
  }
  const q = withFocusPiece(kept, pieceId).toString();
  return `${url.origin}${url.pathname}${q ? `?${q}` : ""}`;
}
